import {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
} from "react";
import * as Location from "expo-location";

type LocationStatus = "idle" | "loading" | "granted" | "denied";

type LocationContextValue = {
  location: { lat: number; lng: number } | null;
  status: LocationStatus;
  requestLocation: () => Promise<void>;
};

const LocationContext = createContext<LocationContextValue | null>(null);

export default function LocationProvider({ children }: PropsWithChildren) {
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [status, setStatus] = useState<LocationStatus>("idle");

  const requestLocation = async () => {
    setStatus("loading");
    const { status: permStatus } =
      await Location.requestForegroundPermissionsAsync();
    if (permStatus !== "granted") {
      setStatus("denied");
      return;
    }
    const loc = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.Balanced,
    });
    setLocation({ lat: loc.coords.latitude, lng: loc.coords.longitude });
    setStatus("granted");
  };

  return (
    <LocationContext.Provider value={{ location, status, requestLocation }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error(
      "useLocationContext must be used within a LocationProvider"
    );
  }
  return context;
}
